import api from './axios';
import analyticsService from './analytics';

// Remove the axios instance creation since we're using the shared one
// const api = axios.create({ ... });

// Remove the request interceptor since it's handled by the shared instance
// api.interceptors.request.use(...);

// Keep the response interceptor for logging
api.interceptors.response.use(
  response => {
    return response;
  },
  error => {
    console.error('Request failed:', {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      data: error.response?.data,
      message: error.message
    });
    return Promise.reject(error);
  }
);

export const getChatHistory = async (page = 1, limit = 20) => {
  try {
    const response = await api.get(`/chat/history?page=${page}&limit=${limit}`);
    
    if (!response.data?.data?.chats) {
      throw new Error('Invalid response format: missing chats data');
    }
    
    const result = {
      chats: response.data.data.chats,
      pagination: response.data.data.pagination
    };
    
    return result;
  } catch (error) {
    console.error('Error in getChatHistory:', {
      error: error.message,
      response: error.response?.data,
      status: error.response?.status
    });
    throw error;
  }
};

const STREAM_ENDPOINT = `${api.defaults.baseURL || ''}/chat/stream`;

const readEventStream = async (response, { onToken, onResult, onStreamError } = {}) => {
  if (!response.body) {
    throw new Error('Streaming is not supported in this browser');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';
  let finalResult = null;

  const processEvent = (rawEvent) => {
    if (!rawEvent.trim()) return;
    const lines = rawEvent.split('\n');
    let eventName = 'message';
    let dataPayload = '';

    lines.forEach(line => {
      if (line.startsWith('event:')) {
        eventName = line.replace('event:', '').trim();
      } else if (line.startsWith('data:')) {
        const value = line.replace('data:', '').trim();
        dataPayload += value;
      }
    });

    let parsedData = null;
    if (dataPayload) {
      try {
        parsedData = JSON.parse(dataPayload);
      } catch {
        parsedData = dataPayload;
      }
    }

    switch (eventName) {
      case 'token':
        if (parsedData?.content && onToken) {
          onToken(parsedData.content);
        }
        break;
      case 'result':
        finalResult = parsedData;
        if (onResult) {
          onResult(parsedData);
        }
        break;
      case 'error':
        if (onStreamError) {
          onStreamError(parsedData);
        }
        throw new Error(parsedData?.message || 'Streaming error occurred');
      case 'done':
        break;
      default:
        break;
    }
  };

  while (true) {
    const { value, done } = await reader.read();
    if (done) {
      buffer += decoder.decode();
      break;
    }
    buffer += decoder.decode(value, { stream: true });
    let eventBoundary = buffer.indexOf('\n\n');
    while (eventBoundary !== -1) {
      const rawEvent = buffer.slice(0, eventBoundary);
      buffer = buffer.slice(eventBoundary + 2);
      processEvent(rawEvent);
      eventBoundary = buffer.indexOf('\n\n');
    }
  }

  if (buffer.trim()) {
    processEvent(buffer);
  }

  return finalResult;
};

const handleChatError = (error, chatId) => {
  console.error('Error in sendMessage:', {
    error: error.message,
    response: error.response?.data,
    status: error.response?.status,
    chatId
  });

  analyticsService.trackChatInteraction('message_failed', {
    chat_id: chatId || 'new_chat',
    error_type: error.response?.status === 429 ? 'rate_limit' : 'other',
    error_message: error.message
  });

  if (error.response?.data?.message) {
    throw new Error(error.response.data.message);
  } else if (error.response?.status === 429) {
    if (chatId) {
      throw new Error('You have hit the message limit in this chat. Please start a new chat or continue in another one with less than 5 messages from you.');
    } else {
      throw new Error('You have exhausted the chat limit for the month. Please upgrade the plan or wait for the monthly reset.');
    }
  } else {
    throw error;
  }
};

export const sendMessage = async (message, chatId = null, communicationStyle = 'professional', options = {}) => {
  const { stream = false, onToken, onStreamResult, onStreamError, attachments = [] } = options;
  const hasAttachments = Array.isArray(attachments) && attachments.length > 0;

  if (!stream) {
    try {
      const response = await api.post('/chat', { 
        message,
        chatId,
        type: 'user',
        communicationStyle,
        attachments
      });
      
      if (!response.data?.data?.message) {
        throw new Error('Invalid response format: missing message data');
      }
      
      analyticsService.trackChatInteraction('message_sent', {
        chat_id: chatId || 'new_chat',
        message_length: message.length,
        has_attachments: hasAttachments,
        is_new_chat: !chatId
      });
      
      return response.data.data;
    } catch (error) {
      handleChatError(error, chatId);
    }
  }

  try {
    const response = await fetch(STREAM_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify({
        message,
        chatId,
        communicationStyle,
        attachments
      })
    });

    if (!response.ok) {
      const errorPayload = await response.json().catch(() => ({}));
      const error = new Error(errorPayload?.message || 'Failed to start streaming response');
      error.response = { status: response.status, data: errorPayload };
      throw error;
    }

    const result = await readEventStream(response, {
      onToken,
      onResult: onStreamResult,
      onStreamError
    });

    analyticsService.trackChatInteraction('message_sent', {
      chat_id: chatId || 'new_chat',
      message_length: message.length,
      has_attachments: hasAttachments,
      is_new_chat: !chatId
    });

    if (!result) {
      throw new Error('Streaming completed without a result');
    }

    return result;
  } catch (error) {
    handleChatError(error, chatId);
  }
};

export const getChatMessages = async (chatId, options = {}) => {
  try {
    const { before, limit = 20 } = options;
    const params = new URLSearchParams();
    
    if (before) {
      params.append('before', before);
    }
    if (limit) {
      params.append('limit', limit);
    }
    
    const queryString = params.toString();
    const url = `/chat/${chatId}/messages${queryString ? `?${queryString}` : ''}`;
    
    const response = await api.get(url);
    
    if (!response.data?.data?.messages) {
      throw new Error('Invalid response format: missing messages data');
    }
    
    return response.data.data.messages;
  } catch (error) {
    console.error('Error in getChatMessages:', {
      error: error.message,
      response: error.response?.data,
      status: error.response?.status,
      chatId,
      options
    });
    throw error;
  }
};

export const createChat = async (title = 'New Chat') => {
  try {
    const response = await api.post('/chat', { title });
    
    if (!response.data?.data?.chat) {
      throw new Error('Invalid response format: missing chat data');
    }
    
    return response.data.data.chat;
  } catch (error) {
    console.error('Error in createChat:', {
      error: error.message,
      response: error.response?.data,
      status: error.response?.status
    });
    throw error;
  }
};

export const deleteChat = async (chatId) => {
  try {
    const response = await api.delete(`/chat/${chatId}`);
    return response.data;
  } catch (error) {
    console.error('Error in deleteChat:', {
      error: error.message,
      response: error.response?.data,
      status: error.response?.status,
      chatId
    });
    throw error;
  }
}; 